import type { EventPayload, PixelConfig } from "./types.js";
import { dntEnabled, gpcEnabled } from "./privacy.js";

export interface SendOptions {
  beacon?: boolean;
}

export interface Transport {
  send(payload: EventPayload, opts?: SendOptions): void;
}

export function createTransport(cfg: PixelConfig): Transport {
  const url = `${cfg.collectorBase}/v1/events`;

  const buildHeaders = (): Record<string, string> => {
    const h: Record<string, string> = {
      "Content-Type": "application/json",
      "X-FS-Pixel-Id": cfg.pixelId,
    };
    if (gpcEnabled() || dntEnabled()) h["Sec-GPC"] = "1";
    return h;
  };

  const RETRY_DELAYS = [250, 1000];
  const nextDelay = (attempt: number): number => {
    const base = RETRY_DELAYS[attempt] ?? 0;
    const jitter = Math.floor((Math.random() - 0.5) * base * 0.4);
    return Math.max(0, base + jitter);
  };

  const tryFetch = async (body: string, attempt: number): Promise<void> => {
    try {
      const res = await fetch(url, {
        method: "POST",
        body,
        keepalive: true,
        headers: buildHeaders(),
        mode: "cors",
        credentials: "omit",
      });
      if (res.status >= 500 && attempt < RETRY_DELAYS.length) {
        setTimeout(() => {
          void tryFetch(body, attempt + 1);
        }, nextDelay(attempt));
      }
    } catch {
      if (attempt < RETRY_DELAYS.length) {
        setTimeout(() => {
          void tryFetch(body, attempt + 1);
        }, nextDelay(attempt));
      }
      // else drop silently
    }
  };

  return {
    send(payload: EventPayload, opts?: SendOptions): void {
      const body = JSON.stringify(payload);
      if (
        opts?.beacon &&
        typeof navigator !== "undefined" &&
        typeof navigator.sendBeacon === "function"
      ) {
        try {
          const blob = new Blob([body], { type: "application/json" });
          const ok = navigator.sendBeacon(url, blob);
          if (ok) return;
        } catch {
          // fall through to fetch
        }
      }
      void tryFetch(body, 0);
    },
  };
}
